import { debounce } from "../../shared/event/debounce";

/**
 * Client cache based on localStorage
 *
 * @author bgr
 */
export class Cache {
	constructor(storageKey, ttl) {
		this.storageKey = storageKey;
		this.ttl = ttl;

		try {
			this.cache = JSON.parse(localStorage.getItem(this.storageKey));
		} catch {
			this.cache = null;
		}

		if (
			!this.cache ||
			!this.cache.hasOwnProperty("values") ||
			!this.cache.hasOwnProperty("queue")
		) {
			this.cache = {
				values: {},
				queue: [],
			};
		}

		/**
		 * Save cache to localStorage throtteling to prevent too much heavy json encoding
		 */
		this._save = debounce(500, () => {
			try {
				localStorage.setItem(this.storageKey, JSON.stringify(this.cache));
			} catch (e) {
				console.info(
					`Failed writing to localStorage for ${this.storageKey}. Got ${e}`,
				);
			}
		});
	}

	/**
	 * Get cached value for given key
	 * or undefined if no set or invalid
	 */
	get(key) {
		if (
			this.cache.values.hasOwnProperty(key) &&
			this.cache.values[key].time + this.ttl >= Date.now()
		) {
			return this.cache.values[key].value;
		}
	}

	/**
	 * Put a value into cache for a given key
	 * Exiting entries will be overwritten
	 */
	set(key, value) {
		// Delete existing time in queue
		if (this.cache.values.hasOwnProperty(key)) {
			let previousTime = this.cache.values[key].time;
			let expiredTime = Date.now() - this.ttl;
			if (previousTime <= expiredTime) {
				// Entry expired
				this.gc(expiredTime);
			} else {
				// Entry not expired
				// Search for first queue entry with same time
				let lower = 0,
					upper = this.cache.queue.length - 1;
				outerLoop: while (lower <= upper) {
					let center = Math.floor((lower + upper) / 2);
					if (this.cache.queue[center].time < previousTime) {
						lower = center + 1;
					} else if (
						center > 0 &&
						this.cache.queue[center - 1].time >= previousTime
					) {
						upper = center - 1;
					} else {
						// Find specific key (there can be several entries with same time)
						while (
							center < this.cache.queue.length &&
							this.cache.queue[center].time == previousTime
						) {
							if (this.cache.queue[center].key == key) {
								this.cache.queue.splice(center, 1);
								break outerLoop;
							}
							center++;
						}
					}
				}
			}
		}

		let time = Date.now();
		this.cache.queue.push({ key, time });
		this.cache.values[key] = { value, time };

		this._save();
	}

	/**
	 * Garbage collection
	 * Deletes invalid entries
	 */
	gc(expiredTime = Date.now() - this.ttl) {
		// all entries not expired?
		if (
			this.cache.queue.length == 0 ||
			this.cache.queue[0].time > expiredTime
		) {
			return;
		}

		let expiredIndex = 0,
			validIndex = this.cache.queue.length - 1;

		// all entries expired?
		if (this.cache.queue[validIndex].time <= expiredTime) {
			this.cache.values = {};
			this.cache.queue = [];
			return;
		}

		while (expiredIndex + 1 < validIndex) {
			let center = Math.floor((expiredIndex + validIndex) / 2);
			if (this.cache.queue[center].time <= expiredTime) {
				expiredIndex = center;
			} else {
				validIndex = center;
			}
		}

		for (let i = 0; i <= expiredIndex; i++) {
			delete this.cache.values[this.cache.queue[i].key];
		}
		this.cache.queue.splice(0, expiredIndex + 1);

		this._save();
	}
}
